// The brackets a flat condition list already has, whether or not it shows them.
//
// Signal conditions each carry their own connector, and `parseConditionsToString`
// joins them with no parentheses at all:
//
//   acc + (index ? ` ${direction} ` : '') + condition
//
// SQL then applies its own precedence, where AND binds tighter than OR. So a
// list reading
//
//   industry is Software
//   OR industry is Banks
//   AND country is United States
//
// evaluates as `Software OR (Banks AND US)`, and a Software company in Germany
// matches it. The editor draws that list as three equal rows, which invites
// exactly the reading the SQL does not honour.
//
// The stored shape is therefore already a disjunction of conjunctions, and
// nothing needs to change about it. What was missing is the drawing. These
// helpers derive the grouping from the connectors that are already there, so the
// editor can show the brackets instead of implying different ones.

/** A condition as far as grouping is concerned. */
export interface Connected {
  /**
   * The connector that joins this condition to the one BEFORE it. Absent on the
   * first, where there is nothing to join to. `parseConditionsToString` defaults
   * a missing one to AND, so this does too.
   */
  direction?: string;
}

const isOr = (condition: Connected) =>
  String(condition?.direction ?? 'AND').toUpperCase() === 'OR';

/**
 * The list split into the groups it actually evaluates as: each group is a run of
 * AND, and the groups are ORed together.
 *
 * The connector belongs to the condition that follows it, so an OR starts a new
 * group and an AND extends the current one. The first condition's connector is
 * ignored, since there is nothing before it to join.
 */
export function groupByConnector<T extends Connected>(conditions: T[]): T[][] {
  return (conditions ?? []).reduce<T[][]>((groups, condition, index) => {
    if (index === 0 || !isOr(condition)) {
      if (groups.length === 0) return [[condition]];
      groups[groups.length - 1].push(condition);
      return groups;
    }
    groups.push([condition]);
    return groups;
  }, []);
}

/**
 * Whether the grouping is worth drawing at all.
 *
 * One group, or groups of one, need no brackets: the list reads correctly as it
 * stands. Brackets earn their space only where a run of ANDs sits inside an OR,
 * which is the case a flat list misrepresents.
 */
export function needsBrackets<T extends Connected>(conditions: T[]): boolean {
  const groups = groupByConnector(conditions);
  return groups.length > 1 && groups.some((group) => group.length > 1);
}

/**
 * Truth of the list under SQL's own precedence, given each condition's result.
 *
 * Exists to be tested against the grouping rather than to be called by the
 * editor: if these two ever disagree, the brackets on screen are wrong and the
 * grouping is the thing to fix.
 */
export function evaluateFlat<T extends Connected>(
  conditions: T[],
  results: boolean[]
): boolean {
  return groupByConnector(
    conditions.map((condition, index) => ({ ...condition, index }))
  ).some((group) => group.every((entry) => results[entry.index]));
}
